import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCart, addToCart as apiAddToCart, updateCartItem as apiUpdateCartItem, removeFromCart as apiRemoveFromCart, clearCart as apiClearCart } from '../services/api';
import { Cart } from '../types';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  addToCart: (itemId: string, quantity: number, itemDetails?: { name: string; price: number; image: string; isVeg: boolean }) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();

  const refreshCart = useCallback(async (showLoading = false) => {
    if (!user) {
        setCart(null);
        return;
    }
    
    if (showLoading) setLoading(true);
    
    try {
      const response = await getCart();
      setCart(response.data.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
        refreshCart(true); // Initial load with loading state
    } else {
        setCart(null);
    }
  }, [user]); // refreshCart dependency causing loop if included without care? useCallback handles it.

  // To avoid infinite loop if refreshCart changes, we trust user dependency.
  // Actually, including refreshCart in deps is correct since it depends on user.

  const addToCart = async (itemId: string, quantity: number, itemDetails?: { name: string; price: number; image: string; isVeg: boolean }) => {
    // Optimistic update - we'll update the UI immediately
    const previousCart = cart;
    
    try {
      // Make the API call
      await apiAddToCart(itemId, quantity);
      
      // Optimistically update the cart without waiting for a refresh
      // The backend will have the correct state, we'll sync on next refresh
      if (cart) {
        const existingItemIndex = cart.items.findIndex(item => item.itemId === itemId);
        const updatedItems = [...cart.items];
        
        if (existingItemIndex >= 0) {
          // Item exists, increment quantity
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: updatedItems[existingItemIndex].quantity + quantity
          };
        } else if (itemDetails) {
          // New item - use provided details for optimistic update
          updatedItems.push({
            itemId,
            name: itemDetails.name,
            price: itemDetails.price,
            quantity,
            image: itemDetails.image,
            isVeg: itemDetails.isVeg
          });
        }
        
        // Recalculate total
        const totalAmount = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        setCart({
          ...cart,
          items: updatedItems,
          totalAmount
        });
      }
      
      showToast('Item added to cart', 'success');
    } catch (error) {
      console.error('Add to cart error:', error);
      showToast('Failed to add item', 'error');
      // Revert to previous state on error
      setCart(previousCart);
      throw error;
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    const previousCart = cart;
    
    try {
      // Optimistically update the UI
      if (cart) {
        const updatedItems = cart.items.map(item => 
          item.itemId === itemId ? { ...item, quantity } : item
        );
        
        const totalAmount = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        setCart({
          ...cart,
          items: updatedItems,
          totalAmount
        });
      }
      
      // Make the API call
      await apiUpdateCartItem(itemId, quantity);
    } catch (error) {
       console.error('Update quantity error:', error);
       showToast('Failed to update quantity', 'error');
       // Revert to previous state on error
       setCart(previousCart);
       throw error;
    }
  };

  const removeFromCart = async (itemId: string) => {
      const previousCart = cart;
      
      try {
          // Optimistically update the UI
          if (cart) {
            const updatedItems = cart.items.filter(item => item.itemId !== itemId);
            const totalAmount = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            
            setCart({
              ...cart,
              items: updatedItems,
              totalAmount
            });
          }
          
          // Make the API call
          await apiRemoveFromCart(itemId);
          showToast('Item removed', 'success');
      } catch (error) {
          console.error('Remove error:', error);
          showToast('Failed to remove item', 'error');
          // Revert to previous state on error
          setCart(previousCart);
          throw error;
      }
  };

  const clearCart = async () => {
      const previousCart = cart;
      
      try {
          // Optimistically update the UI
          if (cart) {
            setCart({
              ...cart,
              items: [],
              totalAmount: 0
            });
          }
          
          // Make the API call
          await apiClearCart();
          showToast('Cart cleared', 'success');
      } catch (error) {
          console.error('Clear error:', error);
          showToast('Failed to clear cart', 'error');
          // Revert to previous state on error
          setCart(previousCart);
          throw error;
      }
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, refreshCart }}>
      {children}
    </CartContext.Provider>
  );
};
